import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Send, Edit2, Trash2, Plus, Minus, AlertCircle, ChevronDown } from 'lucide-react';
import { Service, ServiceCategory, createService, getServices, createServiceCategory } from '../lib/services';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import TokenPurchaseModal from './TokenPurchaseModal';

interface AIServiceCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onServicesCreated: (services: Service[]) => void;
  businessId: string;
  categories: ServiceCategory[];
  onCategoryCreated?: () => void; // Add callback to refresh categories
}

interface GeneratedService {
  name: string;
  description: string;
  category_id: string | null;
  price_options: Array<{
    name: string;
    price: number;
    duration_minutes: number;
  }>;
  add_ons: Array<{
    name: string;
    description: string;
    price: number;
    duration_minutes: number;
  }>;
}

interface AIMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export default function AIServiceCreatorModal({
  isOpen,
  onClose,
  onServicesCreated,
  businessId,
  categories,
  onCategoryCreated
}: AIServiceCreatorModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [conversation, setConversation] = useState<AIMessage[]>([]);
  const [generatedServices, setGeneratedServices] = useState<GeneratedService[]>([]);
  const [existingServices, setExistingServices] = useState<Service[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const [awaitingMoreInfo, setAwaitingMoreInfo] = useState(false);
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState<number | null>(null);
  const [addingCategoryForService, setAddingCategoryForService] = useState<number | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const categoryDropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Load existing services when modal opens
  useEffect(() => {
    if (isOpen && businessId) {
      loadExistingServices();
    }
  }, [isOpen, businessId]);

  const loadExistingServices = async () => {
    try {
      const services = await getServices(businessId);
      setExistingServices(services);
    } catch (error) {
      console.error('Error loading existing services:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Initial greeting
      setConversation([{
        role: 'assistant',
        content: "Hi! I'll help you create services using AI. Tell me what services you'd like to create. For example: 'Create 3 detailing packages: Gold, Silver, and Basic'"
      }]);
    } else {
      setTimeout(() => {
        setIsAnimating(false);
        setConversation([]);
        setGeneratedServices([]);
        setPrompt('');
        setError(null);
        setAwaitingMoreInfo(false);
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!prompt.trim() || isGenerating) return;

    const userMessage = prompt.trim();
    setPrompt('');
    setError(null);

    // Add user message to conversation
    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsGenerating(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('ai-create-services', {
        body: {
          prompt: userMessage,
          conversationHistory: conversation,
          businessId: selectedBusiness?.id,
          businessName: selectedBusiness?.business_name,
          categories: categories.map(c => ({ id: c.id, name: c.name })),
          existingServicesInSession: generatedServices,
          allExistingServices: existingServices.map(s => ({
            name: s.name,
            description: s.description,
            category: s.category?.name || null,
            price_options: s.price_options?.map(po => ({
              name: po.name,
              price: po.price,
              duration_minutes: po.duration_minutes
            })) || []
          }))
        }
      });

      // Check for insufficient tokens
      if (data?.insufficientTokens) {
        setShowTokenPurchaseModal(true);
        setError('Insufficient tokens. Please purchase more to continue.');
        setIsGenerating(false);
        return;
      }

      if (invokeError) throw invokeError;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to generate services');
      }

      // Add AI response to conversation
      if (data.message) {
        setConversation(prev => [...prev, { role: 'assistant', content: data.message }]);
      }

      // Check if AI needs more information
      if (data.needsMoreInfo) {
        setAwaitingMoreInfo(true);
      } else {
        setAwaitingMoreInfo(false);
        
        // If services were generated, update the preview
        if (data.services && data.services.length > 0) {
          setGeneratedServices(data.services);
        }
      }

    } catch (error: any) {
      console.error('Error generating services:', error);
      setError(error.message || 'Failed to generate services');
      setConversation(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${error.message || 'Please try again.'}` 
      }]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateServices = async () => {
    if (generatedServices.length === 0) return;

    setIsCreating(true);
    setError(null);

    try {
      console.log('🚀 Creating services:', generatedServices);
      const createdServices: Service[] = [];

      for (const serviceData of generatedServices) {
        console.log('Creating service:', serviceData.name);
        const service = await createService(
          {
            business_id: businessId,
            name: serviceData.name,
            description: serviceData.description,
            category_id: serviceData.category_id || undefined,
            color: '#000000',
            tax_exempt: false,
            tax_rate_id: null,
            is_membership: false,
          },
          serviceData.price_options,
          serviceData.add_ons.length > 0 ? serviceData.add_ons.map((addon, index) => ({
            name: addon.name,
            description: addon.description,
            price: addon.price,
            duration_minutes: addon.duration_minutes,
            business_id: businessId,
            order_index: index,
            addon_service_id: 'temp_id'
          })) : undefined
        );

        console.log('✅ Service created:', service.name);
        createdServices.push(service);
      }

      console.log('✅ All services created successfully:', createdServices.length);
      
      // Call the success callback with created services
      onServicesCreated(createdServices);
      
      // Close the modal
      onClose();
    } catch (error: any) {
      console.error('❌ Error creating services:', error);
      setError(error.message || 'Failed to create services');
    } finally {
      setIsCreating(false);
    }
  };

  const updateService = (index: number, field: string, value: any) => {
    setGeneratedServices(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updatePriceOption = (serviceIndex: number, priceIndex: number, field: string, value: any) => {
    setGeneratedServices(prev => {
      const updated = [...prev];
      const priceOptions = [...updated[serviceIndex].price_options];
      priceOptions[priceIndex] = { ...priceOptions[priceIndex], [field]: value };
      updated[serviceIndex] = { ...updated[serviceIndex], price_options: priceOptions };
      return updated;
    });
  };

  const addPriceOption = (serviceIndex: number) => {
    setGeneratedServices(prev => {
      const updated = [...prev];
      updated[serviceIndex].price_options.push({
        name: '',
        price: 0,
        duration_minutes: 0
      });
      return updated;
    });
  };

  const removePriceOption = (serviceIndex: number, priceIndex: number) => {
    setGeneratedServices(prev => {
      const updated = [...prev];
      updated[serviceIndex].price_options = updated[serviceIndex].price_options.filter((_, i) => i !== priceIndex);
      return updated;
    });
  };

  const removeService = (index: number) => {
    setGeneratedServices(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateCategory = async (serviceIndex: number) => {
    if (!newCategoryName.trim() || !businessId) return;

    try {
      const newCategory = await createServiceCategory({
        business_id: businessId,
        name: newCategoryName.trim(),
        description: '',
        order_index: categories.length,
        is_membership: false
      });

      console.log('✅ New category created:', newCategory);

      // Update the service with the new category ID
      updateService(serviceIndex, 'category_id', newCategory.id);
      
      // Reset state
      setAddingCategoryForService(null);
      setNewCategoryName('');
      
      // Trigger parent to refresh categories list
      if (onCategoryCreated) {
        await onCategoryCreated();
      }
      
      console.log('✅ Category assigned to service');
    } catch (error) {
      console.error('Error creating category:', error);
      setError('Failed to create category');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openCategoryDropdown !== null) {
        const dropdown = categoryDropdownRefs.current.get(openCategoryDropdown);
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setOpenCategoryDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCategoryDropdown]);

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100400,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        className={`bg-[#1e1e1e] shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[800px] lg:w-[900px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100410,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-6 relative flex items-center justify-between border-b border-blue-400/20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI Service Creator</h2>
              <p className="text-sm text-blue-100 mt-0.5">
                Let Nexus AI help you create professional services
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-white/80 hover:text-white rounded-full hover:bg-white/10 active:bg-white/20 active:scale-95 transition-all duration-150"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col bg-[#1e1e1e]">
          {/* Conversation Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#2a2a2a]">
            {conversation.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-[#3a3a3a] border border-gray-700 text-gray-100'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}

            {isGenerating && (
              <div className="flex justify-start">
                <div className="bg-[#3a3a3a] border border-gray-700 rounded-2xl px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Generated Services Preview */}
          {generatedServices.length > 0 && (
            <div className="border-t border-gray-700 bg-[#1e1e1e] p-6 max-h-[40vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-100">Generated Services ({generatedServices.length})</h3>
                <button
                  onClick={handleCreateServices}
                  disabled={isCreating}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 active:scale-95 transition-all duration-150 disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span className="text-sm font-semibold">Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span className="text-sm font-semibold">Create All Services</span>
                    </>
                  )}
                </button>
              </div>

              <div className="space-y-4">
                {generatedServices.map((service, serviceIndex) => (
                  <div key={serviceIndex} className="bg-[#2a2a2a] border border-gray-700 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={service.name}
                          onChange={(e) => updateService(serviceIndex, 'name', e.target.value)}
                          className="w-full text-lg font-bold text-gray-100 bg-[#3a3a3a] border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Service Name"
                        />
                      </div>
                      <button
                        onClick={() => removeService(serviceIndex)}
                        className="ml-3 p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <textarea
                      value={service.description}
                      onChange={(e) => {
                        updateService(serviceIndex, 'description', e.target.value);
                        // Auto-resize textarea
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      className="w-full text-sm text-gray-200 bg-[#3a3a3a] border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3 resize-none overflow-hidden"
                      placeholder="Service Description"
                      rows={Math.max(3, Math.ceil(service.description.split('\n').length))}
                      style={{
                        minHeight: '60px',
                        height: 'auto'
                      }}
                      ref={(textarea) => {
                        if (textarea) {
                          textarea.style.height = 'auto';
                          textarea.style.height = textarea.scrollHeight + 'px';
                        }
                      }}
                    />

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-400 mb-1">Category *</label>
                      {addingCategoryForService !== serviceIndex ? (
                        <div 
                          className="relative" 
                          ref={(el) => {
                            if (el) categoryDropdownRefs.current.set(serviceIndex, el);
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setOpenCategoryDropdown(openCategoryDropdown === serviceIndex ? null : serviceIndex)}
                            className="w-full text-sm bg-[#3a3a3a] text-gray-100 border border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex items-center justify-between"
                          >
                            <span className={service.category_id ? 'text-gray-100' : 'text-gray-500'}>
                              {service.category_id 
                                ? categories.find(c => c.id === service.category_id)?.name || 'Select a category...'
                                : 'Select a category...'
                              }
                            </span>
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${openCategoryDropdown === serviceIndex ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {openCategoryDropdown === serviceIndex && (
                            <div className="absolute left-0 right-0 mt-2 max-h-48 overflow-y-auto bg-[#2a2a2a] rounded-lg shadow-lg border border-gray-600 z-50">
                              {categories.map(cat => (
                                <button
                                  key={cat.id}
                                  type="button"
                                  onClick={() => {
                                    updateService(serviceIndex, 'category_id', cat.id);
                                    setOpenCategoryDropdown(null);
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm text-gray-200 hover:bg-[#3a3a3a] transition-colors"
                                >
                                  {cat.name}
                                </button>
                              ))}
                              <div className="border-t border-gray-600">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setAddingCategoryForService(serviceIndex);
                                    setOpenCategoryDropdown(null);
                                    setNewCategoryName('');
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm text-blue-400 hover:bg-blue-500/10 font-semibold flex items-center"
                                >
                                  <Plus className="h-3 w-3 mr-2" />
                                  + Add category
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-400/30">
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleCreateCategory(serviceIndex);
                                }
                              }}
                              placeholder="Enter category name"
                              className="w-full px-2 py-1 bg-[#3a3a3a] text-gray-100 border border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <div className="mt-2 flex justify-end space-x-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setAddingCategoryForService(null);
                                  setNewCategoryName('');
                                }}
                                className="px-2 py-1 text-xs text-gray-400 hover:text-gray-200 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCreateCategory(serviceIndex)}
                                disabled={!newCategoryName.trim()}
                                className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-semibold text-gray-400">Price Options</label>
                        <button
                          onClick={() => addPriceOption(serviceIndex)}
                          className="text-xs text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                        >
                          <Plus className="h-3 w-3" />
                          <span>Add Option</span>
                        </button>
                      </div>
                      <div className="space-y-2">
                        {service.price_options.map((option, priceIndex) => (
                          <div key={priceIndex} className="flex items-center space-x-2 bg-[#3a3a3a] border border-gray-600 rounded-lg p-2">
                            <input
                              type="text"
                              value={option.name}
                              onChange={(e) => updatePriceOption(serviceIndex, priceIndex, 'name', e.target.value)}
                              className="flex-1 text-xs text-gray-100 bg-transparent focus:outline-none"
                              placeholder="Option Name"
                            />
                            <input
                              type="number"
                              value={option.price}
                              onChange={(e) => updatePriceOption(serviceIndex, priceIndex, 'price', parseFloat(e.target.value) || 0)}
                              className="w-20 text-xs text-gray-100 bg-transparent focus:outline-none text-right"
                              placeholder="Price"
                              step="0.01"
                            />
                            <input
                              type="number"
                              value={option.duration_minutes}
                              onChange={(e) => updatePriceOption(serviceIndex, priceIndex, 'duration_minutes', parseInt(e.target.value) || 0)}
                              className="w-16 text-xs text-gray-100 bg-transparent focus:outline-none text-right"
                              placeholder="Mins"
                            />
                            {service.price_options.length > 1 && (
                              <button
                                onClick={() => removePriceOption(serviceIndex, priceIndex)}
                                className="p-1 text-red-400 hover:bg-red-500/10 rounded"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="border-t border-gray-700 bg-red-900/20 px-6 py-3">
              <div className="flex items-center text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-700 bg-[#1e1e1e] p-4">
            <form onSubmit={handleSubmit} className="flex items-center space-x-3">
              <input
                ref={inputRef}
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={awaitingMoreInfo ? "Provide more details..." : "Describe the services you want to create..."}
                className="flex-1 px-4 py-3 bg-[#2a2a2a] border border-gray-700 text-gray-100 placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                disabled={isGenerating}
              />
              <button
                type="submit"
                disabled={!prompt.trim() || isGenerating}
                className="p-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 active:scale-95 transition-all duration-150 disabled:bg-gray-700 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Token Purchase Modal */}
      {showTokenPurchaseModal && (
        <TokenPurchaseModal
          isOpen={showTokenPurchaseModal}
          onClose={() => setShowTokenPurchaseModal(false)}
          businessId={selectedBusiness?.id || ''}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}

